import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Florida Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const FLLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 20,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['20 LU biennial', 'Min 4 LU in Laws, Rules & Florida Building Code', 'Min 2 LU in Wind Mitigation/Hurricane Protection', 'Max 10 LU self-study', 'Reportable by January 31 renewal date'],
      stateBoard: 'Florida Board of Architecture & Interior Design',
      boardUrl: 'https://floridasarchitecture.gov/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 18,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['18 PDH biennial', '1 PDH in Laws and Rules (mandatory)', '1 PDH in Professional Ethics (mandatory)', 'Technical engineering subjects', 'Max 9 PDH self-study'],
      stateBoard: 'Florida Board of Professional Engineers',
      boardUrl: 'https://fbpe.org/'
    },
    {
      id: 'PLS',
      name: 'Professional Surveyor & Mapper',
      icon: '📐',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', '2 PDH in Florida Laws and Rules (mandatory)', 'Surveying and mapping topics', 'Min 12 PDH per year', 'Carryover not permitted'],
      stateBoard: 'Florida Board of Professional Surveyors & Mappers',
      boardUrl: 'https://www.fbpsm.org/'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: ['80 CPE hours biennial', 'Min 20 hours annually', '4 hours Ethics (every 3 years)', '8 hours Accounting & Auditing', 'Max 20 hours self-study per year'],
      stateBoard: 'Florida Board of Accountancy',
      boardUrl: 'https://flboardofaccountancy.gov/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 27,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['27 contact hours biennial', '2 hours Prevention of Medical Errors', '1 hour Domestic Violence', '2 hours Laws and Rules', '2 hours HIV/AIDS (initial license only)', 'Max 15 hours online'],
      stateBoard: 'Florida Board of Nursing',
      boardUrl: 'https://floridasnursing.gov/'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 30,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['30 contact hours biennial', '2 hours Prevention of Medical Errors', '1 hour Domestic Violence', '2 hours Laws and Rules', 'Advanced practice topics', 'Separate from RN CE'],
      stateBoard: 'Florida Board of Nursing',
      boardUrl: 'https://floridasnursing.gov/'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Laws and Rules', '2 hours Prevention of Medical Errors', '1 hour HIV/AIDS', '3 hours Domestic Violence', 'Clinical practice topics'],
      stateBoard: 'Florida Board of Clinical Social Work, Marriage & Family Therapy, and Mental Health Counseling',
      boardUrl: 'https://floridasmentalhealthprofessions.gov/'
    },
    {
      id: 'LMHC',
      name: 'Licensed Mental Health Counselor',
      icon: '💭',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Laws and Rules', '2 hours Prevention of Medical Errors', '1 hour HIV/AIDS', '3 hours Domestic Violence', 'Mental health counseling topics'],
      stateBoard: 'Florida Board of Clinical Social Work, Marriage & Family Therapy, and Mental Health Counseling',
      boardUrl: 'https://floridasmentalhealthprofessions.gov/'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Laws and Rules', '2 hours Prevention of Medical Errors', '1 hour HIV/AIDS', '3 hours Domestic Violence', 'Marriage and family therapy topics'],
      stateBoard: 'Florida Board of Clinical Social Work, Marriage & Family Therapy, and Mental Health Counseling',
      boardUrl: 'https://floridasmentalhealthprofessions.gov/'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Sales Associate',
      icon: '🏠',
      credits: 14,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['14 hours biennial', '3 hours Core Law', '11 hours elective (may include specialty)', 'Completion required before renewal', 'Online courses accepted'],
      stateBoard: 'Florida Real Estate Commission',
      boardUrl: 'https://www.myfloridalicense.com/DBPR/real-estate-commission/'
    },
    {
      id: 'APPR',
      name: 'Certified Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial (Certified)', '14 hours annually', '7-hour National USPAP Update (mandatory)', 'AQB-approved education', 'Completion required before renewal'],
      stateBoard: 'Florida Real Estate Appraisal Board',
      boardUrl: 'https://www.myfloridalicense.com/DBPR/real-estate-appraisal/'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 33,
      cycleYears: 3,
      terminology: 'CLE',
      highlights: ['33 CLE hours triennial (July 1 - June 30)', '5 hours Legal Ethics', '2 hours Bias Elimination', '1 hour Mental Illness Awareness', '3 hours Technology', 'Max 11 hours self-study'],
      stateBoard: 'The Florida Bar - Continuing Legal Education',
      boardUrl: 'https://www.floridabar.org/prof/cle/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Florida Professional License Renewal Requirements",
      "description": "Complete guide to Florida professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/fl-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.myfloridalicense.com/",
          "https://www.floridabar.org/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Florida Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Florida License Renewal",
            "item": "https://cectrack.com/fl-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Florida professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Florida. Architects need 20 LU biennial, Engineers need 18 PDH biennial, Surveyors need 24 PDH biennial, CPAs need 80 CPE hours biennial, RNs need 27 contact hours biennial, APRNs need 30 contact hours biennial, Mental Health professionals need 30 hours biennial, Real Estate professionals need 14 hours biennial, and Attorneys need 33 CLE hours triennial."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Florida?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Florida Department of Business and Professional Regulation (DBPR) oversees most professional licenses including Architects, Engineers, Surveyors, CPAs, Nurses, Mental Health professionals, and Real Estate professionals. The Florida Bar administers attorney CLE requirements separately."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Florida professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Florida has extensive mandatory CE requirements: Architects need 4 LU in Laws/Building Code and 2 LU in Wind Mitigation; Engineers need 1 PDH in Laws/Rules and 1 PDH in Ethics; Surveyors need 2 PDH in Laws/Rules; CPAs need 4 hours Ethics every 3 years; Healthcare professionals need 2 hours Prevention of Medical Errors, 1-3 hours Domestic Violence, 2 hours Laws/Rules, and HIV/AIDS training; Real Estate professionals need 3 hours Core Law; Attorneys need 5 hours Legal Ethics, 2 hours Bias Elimination, 1 hour Mental Illness Awareness, and 3 hours Technology."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Florida professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Florida professions renew biennially (every 2 years). Attorneys renew every 3 years (triennially). Each profession has specific renewal dates aligned with their birthday or license expiration date."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Florida Department of Business and Professional Regulation",
      "alternateName": "DBPR",
      "url": "https://www.myfloridalicense.com/",
      "telephone": "+1-850-487-1395",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2601 Blair Stone Road",
        "addressLocality": "Tallahassee",
        "addressRegion": "FL",
        "postalCode": "32399",
        "addressCountry": "US"
      },
      "areaServed": "Florida",
      "description": "Regulates professional licensing for Architects, Engineers, Surveyors, CPAs, Nurses, Mental Health professionals, and Real Estate professionals in Florida"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "The Florida Bar",
      "url": "https://www.floridabar.org/",
      "telephone": "+1-850-561-5600",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "651 East Jefferson Street",
        "addressLocality": "Tallahassee",
        "addressRegion": "FL",
        "postalCode": "32399",
        "addressCountry": "US"
      },
      "areaServed": "Florida",
      "description": "Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Florida"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Florida Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Florida professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Florida license renewal, FL CE requirements, FL continuing education, Florida professional licenses, DBPR, Florida nurses, FL CPAs, FL engineers, FL attorneys"
        canonical="https://cectrack.com/fl-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Florida Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Florida professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0891b2', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0891b2', marginBottom: '0.5rem' }} aria-label="Over 60 categories">60+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0891b2', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>DBPR Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#0891b2', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Florida CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#cffafe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0891b2' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 2 ? 'Biennial' : prof.cycleYears === 3 ? 'Triennial' : `Every ${prof.cycleYears} Years`}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#06b6d4', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Florida License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Florida professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all FL professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#06b6d4', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Florida professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Florida State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Florida professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Florida Department of Business & Professional Regulation
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates Architects, Engineers, Surveyors, CPAs, Nurses, Mental Health professionals, and Real Estate professionals in Florida.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18504871395" aria-label="Call DBPR at 850-487-1395">(850) 487-1395</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.myfloridalicense.com/" target="_blank" rel="noopener noreferrer" aria-label="Visit DBPR website">
                      myfloridalicense.com
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 2601 Blair Stone Road, Tallahassee, FL 32399
                  </div>
                  <a 
                    href="https://www.myfloridalicense.com/intentions2.asp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all DBPR professions"
                  >
                    View All Professions
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    The Florida Bar
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Continuing Legal Education (CLE) requirements for attorneys licensed in Florida. Mandatory triennial CLE includes Ethics, Bias Elimination, Mental Illness Awareness, and Technology.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18505615600" aria-label="Call Florida Bar at 850-561-5600">(850) 561-5600</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.floridabar.org/" target="_blank" rel="noopener noreferrer" aria-label="Visit Florida Bar website">
                      www.floridabar.org
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 651 East Jefferson Street, Tallahassee, FL 32399
                  </div>
                  <a 
                    href="https://www.floridabar.org/prof/cle/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View CLE requirements"
                  >
                    View CLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="FL" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Florida License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Florida who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default FLLicenseRenewal;
